import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import EmailComposer from "../components/profile-components/EmailComposer";
import AddressVerificationFields from "../components/common/AddressVerificationFields";
import ActivityLogPanel from "../components/profile-components/ActivityLogPanel";
import API_BASE_URL from "../config";
import "./SalesPages.css";
import usePageTitle from "../hooks/usePageTitle";

function SubscriberProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [subscriber, setSubscriber] = useState(null);
  const [owners, setOwners] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [workspaceActionLoading, setWorkspaceActionLoading] =
    useState(false);
  const [lifecycleActionLoading, setLifecycleActionLoading] =
    useState(false);
  const [checkingSubdomain, setCheckingSubdomain] =
    useState(false);
  const [subdomainStatus, setSubdomainStatus] =
    useState(null);
  const [isEditingSubscription, setIsEditingSubscription] =
    useState(false);
  const [showEmailComposer, setShowEmailComposer] =
    useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [billingEvents, setBillingEvents] = useState([]);
  const [billingEventsLoading, setBillingEventsLoading] =
    useState(true);
  const [billingEventsError, setBillingEventsError] =
    useState("");

  const subscriberPageTitle =
    subscriber?.business_name ||
    subscriber?.primary_contact_name ||
    "Subscriber Profile";

  usePageTitle(subscriberPageTitle);

  useEffect(() => {
    loadSubscriber();
    loadOwners();
    loadPlans();
    loadBillingEvents();
  }, [id]);

  const normalizeStatusValue = (value) =>
    String(value || "").trim().toLowerCase();

  const formatStatusLabel = (value) => {
    if (!value) return "—";

    return normalizeStatusValue(value)
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map(
        (word) =>
          word.charAt(0).toUpperCase() +
          word.slice(1)
      )
      .join(" ");
  };

  const subscriptionStatus = normalizeStatusValue(
    subscriber?.subscription_status
  );

  const isTrialBilling =
    normalizeStatusValue(subscriber?.billing_status) ===
    "trial";

  const hasScheduledCancellation =
    subscriptionStatus === "cancel_pending" ||
    Boolean(subscriber?.scheduled_cancel_at);

  const isCancelled =
    subscriptionStatus === "cancelled" ||
    subscriptionStatus === "canceled";

  const rootDomain = "agentbookcrm.com";

  const selectedPlan = useMemo(() => {
    return plans.find(
      (plan) =>
        String(plan.id) ===
        String(subscriber?.subscription_plan_id)
    );
  }, [plans, subscriber?.subscription_plan_id]);

  const computedDates = useMemo(() => {
    const sourceDate =
      subscriber?.start_date ||
      subscriber?.trial_starts_at;

    if (!sourceDate) {
      return {
        trialEndDate: subscriber?.trial_ends_at
          ? String(subscriber.trial_ends_at).slice(0, 10)
          : "",
        firstPaymentDate: "",
        billingDayLabel: ""
      };
    }

    const start = new Date(
      `${String(sourceDate).slice(0, 10)}T00:00:00`
    );

    if (Number.isNaN(start.getTime())) {
      return {
        trialEndDate: subscriber?.trial_ends_at
          ? String(subscriber.trial_ends_at).slice(0, 10)
          : "",
        firstPaymentDate: "",
        billingDayLabel: ""
      };
    }

    const trialEnd = new Date(start);
    trialEnd.setDate(trialEnd.getDate() + 29);

    const firstPayment = new Date(start);
    firstPayment.setDate(firstPayment.getDate() + 30);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(
        2,
        "0"
      );
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    const getOrdinalSuffix = (day) => {
      if (day >= 11 && day <= 13) return "th";

      const lastDigit = day % 10;

      if (lastDigit === 1) return "st";
      if (lastDigit === 2) return "nd";
      if (lastDigit === 3) return "rd";

      return "th";
    };

    return {
      trialEndDate: formatDate(trialEnd),
      firstPaymentDate: formatDate(firstPayment),
      billingDayLabel: `${firstPayment.getDate()}${getOrdinalSuffix(
        firstPayment.getDate()
      )} of each month`
    };
  }, [subscriber]);

  const formattedScheduledCancelDate = useMemo(() => {
    if (!subscriber?.scheduled_cancel_at) return "";

    const date = new Date(subscriber.scheduled_cancel_at);

    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  }, [subscriber?.scheduled_cancel_at]);

  const formattedCancelledDate = useMemo(() => {
    if (!subscriber?.cancelled_at) return "";

    const date = new Date(subscriber.cancelled_at);

    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  }, [subscriber?.cancelled_at]);

  const formatBillingEventDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Not available";
    }

    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const formatBillingEventAmount = (event) => {
    const rawAmount =
      event?.amount_paid !== null &&
      event?.amount_paid !== undefined
        ? event.amount_paid
        : event?.amount_due;

    if (
      rawAmount === null ||
      rawAmount === undefined ||
      rawAmount === ""
    ) {
      return "—";
    }

    const numericAmount = Number(rawAmount);

    if (Number.isNaN(numericAmount)) {
      return "—";
    }

    return numericAmount.toLocaleString("en-US", {
      style: "currency",
      currency: String(
        event?.currency || "USD"
      ).toUpperCase()
    });
  };

  const formatBillingEventType = (value) => {
    if (!value) return "Stripe event";

    return String(value)
      .split(".")
      .map((part) =>
        part
          .split("_")
          .map(
            (word) =>
              word.charAt(0).toUpperCase() +
              word.slice(1)
          )
          .join(" ")
      )
      .join(" · ");
  };

  const loadSubscriber = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) {
        throw new Error("Failed to load subscriber");
      }

      const data = await res.json();

      setSubscriber(data);
      setSubdomainStatus(null);
      setIsEditingSubscription(false);
    } catch (err) {
      console.error(
        "Load subscriber error:",
        err.message
      );

      setSubscriber(null);
    } finally {
      setLoading(false);
    }
  };

  const loadBillingEvents = async () => {
    try {
      setBillingEventsLoading(true);
      setBillingEventsError("");

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/${id}/billing/events`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
            "Failed to load billing history"
        );
      }

      setBillingEvents(
        Array.isArray(data.events) ? data.events : []
      );
    } catch (err) {
      console.error(
        "Load subscriber billing events error:",
        err.message
      );

      setBillingEvents([]);
      setBillingEventsError(
        err.message ||
          "Failed to load billing history"
      );
    } finally {
      setBillingEventsLoading(false);
    }
  };

  const loadOwners = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/users/admin/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) return;

      const data = await res.json();

      setOwners(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(
        "Load owners error:",
        err.message
      );
    }
  };

  const loadPlans = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscription-plans`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) return;

      const data = await res.json();

      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(
        "Load plans error:",
        err.message
      );
    }
  };

  const handleChange = (field, value) => {
    setSubscriber((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (fieldName, value) => {
    handleChange(fieldName, value);
  };

  const handleOpenEmailComposer = (email) => {
    setEmailRecipient(
      email || subscriber?.email || ""
    );

    setShowEmailComposer(true);
  };

  const handleCloseEmailComposer = () => {
    setShowEmailComposer(false);
    setEmailRecipient("");
  };

  const checkSubdomain = async (
    value = subscriber?.subdomain || ""
  ) => {
    try {
      const trimmed = String(value || "").trim();

      if (!trimmed) {
        setSubdomainStatus(null);
        return null;
      }

      setCheckingSubdomain(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/check-subdomain?value=${encodeURIComponent(
          trimmed
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
            "Failed to check subdomain"
        );
      }

      const currentSubdomain = String(
        subscriber?.subdomain || ""
      )
        .trim()
        .toLowerCase();

      const normalizedReturned = String(
        data.normalized || ""
      )
        .trim()
        .toLowerCase();

      if (
        currentSubdomain &&
        normalizedReturned &&
        currentSubdomain === normalizedReturned
      ) {
        const ownValueResult = {
          ...data,
          available: true
        };

        setSubdomainStatus(ownValueResult);

        return ownValueResult;
      }

      setSubdomainStatus(data);

      return data;
    } catch (err) {
      console.error(
        "Check subdomain error:",
        err.message
      );

      const failed = {
        available: false,
        reason:
          err.message ||
          "Failed to check subdomain"
      };

      setSubdomainStatus(failed);

      return failed;
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const buildSubscriberPayload = () => {
    const effectiveStartDate =
      subscriber.start_date ||
      subscriber.trial_starts_at ||
      null;

    const effectiveTrialEnd =
      computedDates.trialEndDate ||
      subscriber.trial_ends_at ||
      null;

    return {
      business_name: subscriber.business_name,
      primary_contact_name:
        subscriber.primary_contact_name,
      email: subscriber.email,
      phone: subscriber.phone,
      street_address: subscriber.street_address,
      city: subscriber.city,
      state: subscriber.state,
      zip_code: subscriber.zip_code,
      county: subscriber.county || null,
      address_verified:
        subscriber.address_verified === true ||
        subscriber.address_verified === 1
          ? 1
          : 0,
      npn: subscriber.npn,
      owner_user_id:
        subscriber.owner_user_id || null,
      status: subscriber.status,
      source: subscriber.source,
      plan_name: subscriber.plan_name,
      subscription_plan_id:
        subscriber.subscription_plan_id || null,
      subscription_status:
        subscriber.subscription_status,
      billing_status: subscriber.billing_status,
      billing_frequency:
        subscriber.billing_frequency,
      monthly_amount: subscriber.monthly_amount,
      start_date: effectiveStartDate,
      subdomain: subscriber.subdomain || null,
      full_domain: subscriber.full_domain || null,
      stripe_customer_id:
        subscriber.stripe_customer_id || null,
      stripe_subscription_id:
        subscriber.stripe_subscription_id || null,
      billing_provider:
        subscriber.billing_provider || null,
      next_invoice_date:
        subscriber.next_invoice_date || null,
      trial_ends_at: effectiveTrialEnd,
      subscription_started_at:
        subscriber.subscription_started_at || null,
      is_trial:
        subscriber.billing_status === "trial" ? 1 : 0,
      trial_starts_at: effectiveStartDate,
      billing_contact_name:
        subscriber.billing_contact_name || null,
      billing_contact_email:
        subscriber.billing_contact_email || null,
      invite_sent_at:
        subscriber.invite_sent_at || null,
      invited_owner_email:
        subscriber.invited_owner_email || null,
      cancelled_at:
        subscriber.cancelled_at || null,
      scheduled_cancel_at:
        subscriber.scheduled_cancel_at || null
    };
  };

  const saveSubscriber = async () => {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${API_BASE_URL}/api/subscribers/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(
          buildSubscriberPayload()
        )
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(
        data.message || "Failed to save subscriber"
      );
    }

    return data;
  };

  const handleSaveChanges = async () => {
    try {
      setProfileSaving(true);

      if (
        !subscriber?.business_name ||
        !subscriber.business_name.trim()
      ) {
        throw new Error("Business name is required");
      }

      if (
        !subscriber?.primary_contact_name ||
        !subscriber.primary_contact_name.trim()
      ) {
        throw new Error(
          "Primary contact name is required"
        );
      }

      if (
        !subscriber?.email ||
        !subscriber.email.trim()
      ) {
        throw new Error("Email is required");
      }

      await saveSubscriber();

      alert("Subscriber changes saved.");

      await loadSubscriber();
    } catch (err) {
      console.error(
        "Save subscriber changes error:",
        err.message
      );

      alert(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveSubscription = async () => {
    try {
      setSaving(true);

      if (
        !subscriber?.business_name ||
        !subscriber.business_name.trim()
      ) {
        throw new Error("Business name is required");
      }

      if (
        !subscriber?.primary_contact_name ||
        !subscriber.primary_contact_name.trim() ||
        !subscriber?.email ||
        !subscriber.email.trim() ||
        !subscriber?.subscription_plan_id ||
        !subscriber?.subdomain ||
        !subscriber.subdomain.trim()
      ) {
        throw new Error(
          "Business name, primary contact name, email, plan, and subdomain are required"
        );
      }

      const subdomainResult = await checkSubdomain(
        subscriber.subdomain
      );

      if (!subdomainResult?.available) {
        throw new Error(
          subdomainResult?.reason ||
            "That subdomain is not available"
        );
      }

      await saveSubscriber();

      alert("Subscription updated successfully.");

      setIsEditingSubscription(false);
      await loadSubscriber();
    } catch (err) {
      console.error(
        "Save subscription error:",
        err.message
      );

      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleApproveProvisioning = async () => {
    try {
      if (
        !subscriber?.subdomain ||
        !subscriber.subdomain.trim()
      ) {
        alert(
          "Please enter a subdomain before approving the domain."
        );

        return;
      }

      setWorkspaceActionLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/${id}/approve-provisioning`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            subdomain: subscriber.subdomain
          })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
            "Failed to approve domain"
        );
      }

      alert("Domain approved.");
      loadSubscriber();
    } catch (err) {
      console.error(
        "Approve domain error:",
        err.message
      );

      alert(err.message);
    } finally {
      setWorkspaceActionLoading(false);
    }
  };

  const handleMarkProvisioned = async () => {
    try {
      setWorkspaceActionLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/${id}/mark-provisioned`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
            "Failed to mark workspace ready"
        );
      }

      alert("Workspace marked ready.");
      loadSubscriber();
    } catch (err) {
      console.error(
        "Mark workspace ready error:",
        err.message
      );

      alert(err.message);
    } finally {
      setWorkspaceActionLoading(false);
    }
  };

  const handleActivateSubscriber = async () => {
    try {
      setWorkspaceActionLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/${id}/activate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message || "Failed to go live"
        );
      }

      alert("Subscriber is now live.");
      loadSubscriber();
    } catch (err) {
      console.error(
        "Go live error:",
        err.message
      );

      alert(err.message);
    } finally {
      setWorkspaceActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const confirmed = window.confirm(
        "Schedule this subscription for cancellation at the end of its current Stripe billing period?"
      );

      if (!confirmed) return;

      setLifecycleActionLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/${id}/cancel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
            "Failed to schedule cancellation"
        );
      }

      alert(
        data.message ||
          "Subscription cancellation scheduled."
      );

      await loadSubscriber();
      await loadBillingEvents();
    } catch (err) {
      console.error(
        "Schedule cancellation error:",
        err.message
      );

      alert(err.message);
    } finally {
      setLifecycleActionLoading(false);
    }
  };

  const handleReactivateSubscriber = async () => {
    try {
      const confirmed = window.confirm(
        hasScheduledCancellation
          ? "Remove the scheduled cancellation and keep this subscription active?"
          : "Reactivate this subscriber?\n\nThis will restore the subscriber to active status."
      );

      if (!confirmed) return;

      setLifecycleActionLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/${id}/reactivate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
            "Failed to reactivate subscriber"
        );
      }

      alert(
        hasScheduledCancellation
          ? "Scheduled cancellation removed."
          : "Subscriber reactivated."
      );

      await loadSubscriber();
      await loadBillingEvents();
    } catch (err) {
      console.error(
        "Reactivate subscriber error:",
        err.message
      );

      alert(err.message);
    } finally {
      setLifecycleActionLoading(false);
    }
  };

  const mergeData = {
    business_name:
      subscriber?.business_name || "",
    primary_contact_name:
      subscriber?.primary_contact_name || "",
    email: subscriber?.email || "",
    subdomain: subscriber?.subdomain || ""
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, "-")}`;

  if (loading) {
    return (
      <>
        <Navbar />
        <Topbar />

        <div className="main-content sales-page">
          <div className="sales-section-card">
            Loading subscriber...
          </div>
        </div>
      </>
    );
  }

  if (!subscriber) {
    return (
      <>
        <Navbar />
        <Topbar />

        <div className="main-content sales-page">
          <div className="sales-section-card">
            Subscriber not found.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">
              Agent Book CRM
            </p>

            <h1 className="sales-title">
              {subscriber.business_name ||
                "Subscriber Profile"}
            </h1>

            <p className="sales-subtitle">
              Manage subscriber contact details,
              subscription lifecycle, billing status,
              workspace setup, and communications.
            </p>

            <div className="sales-hero-meta">
              <span
                className={statusClass(
                  subscriber.status || "unknown"
                )}
              >
                {formatStatusLabel(
                  subscriber.status || "unknown"
                )}
              </span>

              {subscriber.subscription_status && (
                <span
                  className={statusClass(
                    subscriber.subscription_status
                  )}
                >
                  {formatStatusLabel(
                    subscriber.subscription_status
                  )}
                </span>
              )}

              {subscriber.billing_status && (
                <span
                  className={statusClass(
                    subscriber.billing_status
                  )}
                >
                  Billing:{" "}
                  {formatStatusLabel(
                    subscriber.billing_status
                  )}
                </span>
              )}

              {isTrialBilling && (
                <span className="sales-chip trial">
                  Trial Billing
                </span>
              )}

              {subscriber.source && (
                <span className="sales-chip sales-chip-neutral">
                  {subscriber.source}
                </span>
              )}

              {(subscriber.plan_name ||
                selectedPlan?.plan_name) && (
                <span className="sales-chip sales-chip-outline">
                  {selectedPlan?.plan_name ||
                    subscriber.plan_name}
                </span>
              )}

              {subscriber.provisioning_status && (
                <span className="sales-chip sales-chip-neutral">
                  {formatStatusLabel(
                    subscriber.provisioning_status
                  )}
                </span>
              )}
            </div>
          </div>

          <div className="sales-header-actions">
            <button
              type="button"
              className="sales-secondary-btn"
              onClick={() =>
                navigate("/subscribers")
              }
            >
              Back to Subscribers
            </button>

            <button
              type="button"
              className="sales-secondary-btn"
              onClick={() =>
                handleOpenEmailComposer(
                  subscriber.email
                )
              }
            >
              Send Email
            </button>

            {(hasScheduledCancellation ||
              isCancelled) && (
              <button
                type="button"
                className="sales-secondary-btn"
                onClick={
                  handleReactivateSubscriber
                }
                disabled={
                  lifecycleActionLoading
                }
              >
                {lifecycleActionLoading
                  ? "Working..."
                  : hasScheduledCancellation
                    ? "Undo Scheduled Cancellation"
                    : "Reactivate Subscriber"}
              </button>
            )}
          </div>
        </div>

        {showEmailComposer ? (
          <EmailComposer
            recipientEmail={
              emailRecipient ||
              subscriber.email ||
              ""
            }
            recipientName={
              subscriber.business_name ||
              subscriber.primary_contact_name ||
              "Subscriber"
            }
            mergeData={mergeData}
            onClose={handleCloseEmailComposer}
          />
        ) : (
          <>
            <div className="sales-profile-grid">
              <div className="sales-section-card sales-section-lead">
                <div className="sales-section-head">
                  <div>
                    <h2>Subscriber Information</h2>

                    <p>
                      Core business, contact, address,
                      and ownership details.
                    </p>
                  </div>
                </div>

                <div className="sales-form-fields">
                  <div className="sales-field sales-field-full">
                    <label>Business Name</label>

                    <input
                      value={
                        subscriber.business_name || ""
                      }
                      onChange={(event) =>
                        handleChange(
                          "business_name",
                          event.target.value
                        )
                      }
                    />
                  </div>

                  <div className="sales-field">
                    <label>
                      Primary Contact Name
                    </label>

                    <input
                      value={
                        subscriber.primary_contact_name ||
                        ""
                      }
                      onChange={(event) =>
                        handleChange(
                          "primary_contact_name",
                          event.target.value
                        )
                      }
                    />
                  </div>

                  <div className="sales-field">
                    <label>Email</label>

                    <input
                      value={subscriber.email || ""}
                      onChange={(event) =>
                        handleChange(
                          "email",
                          event.target.value
                        )
                      }
                    />
                  </div>

                  <div className="sales-field">
                    <label>Phone</label>

                    <input
                      value={subscriber.phone || ""}
                      onChange={(event) =>
                        handleChange(
                          "phone",
                          event.target.value
                        )
                      }
                    />
                  </div>

                  <div className="sales-field">
                    <label>NPN</label>

                    <input
                      value={subscriber.npn || ""}
                      onChange={(event) =>
                        handleChange(
                          "npn",
                          event.target.value
                        )
                      }
                    />
                  </div>

                  <AddressVerificationFields
                    values={subscriber}
                    onChange={handleAddressChange}
                    streetLabel="Street Address"
                    showUnit={false}
                    showCounty={false}
                    fieldMap={{
                      street: "street_address",
                      city: "city",
                      state: "state",
                      zip: "zip_code",
                      county: "county",
                      addressVerified:
                        "address_verified"
                    }}
                  />

                  <div className="sales-field">
                    <label>Owner</label>

                    <select
                      value={
                        subscriber.owner_user_id || ""
                      }
                      onChange={(event) =>
                        handleChange(
                          "owner_user_id",
                          event.target.value
                        )
                      }
                    >
                      <option value="">
                        Select Owner
                      </option>

                      {owners.map((owner) => (
                        <option
                          key={owner.id}
                          value={owner.id}
                        >
                          {owner.name}{" "}
                          {owner.email
                            ? `(${owner.email})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>Status</label>

                    <select
                      value={
                        subscriber.status || "active"
                      }
                      onChange={(event) =>
                        handleChange(
                          "status",
                          event.target.value
                        )
                      }
                    >
                      <option value="active">
                        Active
                      </option>

                      <option value="inactive">
                        Inactive
                      </option>

                      <option value="pending">
                        Pending
                      </option>
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>Source</label>

                    <input
                      value={subscriber.source || ""}
                      onChange={(event) =>
                        handleChange(
                          "source",
                          event.target.value
                        )
                      }
                    />
                  </div>

                  <div className="sales-field">
                    <label>
                      Invited Owner Email
                    </label>

                    <input
                      value={
                        subscriber.invited_owner_email ||
                        ""
                      }
                      onChange={(event) =>
                        handleChange(
                          "invited_owner_email",
                          event.target.value
                        )
                      }
                    />
                  </div>
                </div>

                <div
                  className="sales-header-actions"
                  style={{
                    marginTop: "14px",
                    justifyContent: "flex-end"
                  }}
                >
                  <button
                    type="button"
                    className="sales-primary-btn"
                    onClick={handleSaveChanges}
                    disabled={
                      profileSaving || saving
                    }
                  >
                    {profileSaving
                      ? "Saving..."
                      : "Save Changes"}
                  </button>
                </div>
              </div>

              <div className="sales-section-card sales-section-subscription">
                <div className="sales-section-head">
                  <div>
                    <h2>
                      Subscription Information
                    </h2>

                    <p>
                      Plan, subscription lifecycle,
                      billing state, and payment
                      schedule.
                    </p>
                  </div>

                  <div className="sales-header-actions">
                    {!isEditingSubscription ? (
                      <button
                        type="button"
                        className="sales-secondary-btn"
                        onClick={() =>
                          setIsEditingSubscription(true)
                        }
                      >
                        Edit Subscription
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="sales-secondary-btn"
                          onClick={() => {
                            setIsEditingSubscription(
                              false
                            );

                            loadSubscriber();
                          }}
                          disabled={saving}
                        >
                          Cancel
                        </button>

                        <button
                          type="button"
                          className="sales-primary-btn"
                          onClick={
                            handleSaveSubscription
                          }
                          disabled={saving}
                        >
                          {saving
                            ? "Saving..."
                            : "Save Subscription"}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="sales-form-fields">
                  <div className="sales-field">
                    <label>Plan</label>

                    <select
                      value={
                        subscriber.subscription_plan_id ||
                        ""
                      }
                      onChange={(event) => {
                        const selected = plans.find(
                          (plan) =>
                            String(plan.id) ===
                            event.target.value
                        );

                        handleChange(
                          "subscription_plan_id",
                          event.target.value
                        );

                        handleChange(
                          "plan_name",
                          selected?.plan_name ||
                            subscriber.plan_name ||
                            ""
                        );

                        if (
                          selected?.monthly_price !==
                            undefined &&
                          selected?.monthly_price !== null
                        ) {
                          handleChange(
                            "monthly_amount",
                            selected.monthly_price
                          );
                        }
                      }}
                      disabled={
                        !isEditingSubscription
                      }
                    >
                      <option value="">
                        Select Plan
                      </option>

                      {plans.map((plan) => (
                        <option
                          key={plan.id}
                          value={plan.id}
                        >
                          {plan.plan_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>
                      Subscription Status
                    </label>

                    <select
                      value={
                        subscriber.subscription_status ||
                        "active"
                      }
                      onChange={(event) =>
                        handleChange(
                          "subscription_status",
                          event.target.value
                        )
                      }
                      disabled={
                        !isEditingSubscription
                      }
                    >
                      <option value="active">
                        Active
                      </option>

                      <option value="paused">
                        Paused
                      </option>

                      <option value="cancel_pending">
                        Cancel Pending
                      </option>

                      <option value="cancelled">
                        Cancelled
                      </option>

                      <option value="past_due">
                        Past Due
                      </option>

                      <option value="unpaid">
                        Unpaid
                      </option>

                      <option value="incomplete">
                        Incomplete
                      </option>
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>Billing Status</label>

                    <select
                      value={
                        subscriber.billing_status ||
                        "trial"
                      }
                      onChange={(event) =>
                        handleChange(
                          "billing_status",
                          event.target.value
                        )
                      }
                      disabled={
                        !isEditingSubscription
                      }
                    >
                      <option value="trial">
                        Trial
                      </option>

                      <option value="active">
                        Active
                      </option>

                      <option value="current">
                        Current
                      </option>

                      <option value="past_due">
                        Past Due
                      </option>

                      <option value="unpaid">
                        Unpaid
                      </option>

                      <option value="incomplete">
                        Incomplete
                      </option>

                      <option value="paused">
                        Paused
                      </option>

                      <option value="cancelled">
                        Cancelled
                      </option>
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>
                      Billing Frequency
                    </label>

                    <select
                      value={
                        subscriber.billing_frequency ||
                        ""
                      }
                      onChange={(event) =>
                        handleChange(
                          "billing_frequency",
                          event.target.value
                        )
                      }
                      disabled={
                        !isEditingSubscription
                      }
                    >
                      <option value="">
                        Select Frequency
                      </option>

                      <option value="monthly">
                        Monthly
                      </option>

                      <option value="annual">
                        Annual
                      </option>
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>Monthly Amount</label>

                    <input
                      type="number"
                      step="0.01"
                      value={
                        subscriber.monthly_amount ||
                        ""
                      }
                      onChange={(event) =>
                        handleChange(
                          "monthly_amount",
                          event.target.value
                        )
                      }
                      readOnly={
                        !isEditingSubscription
                      }
                    />
                  </div>

                  <div className="sales-field">
                    <label>
                      Start Date / Trial Start
                    </label>

                    <input
                      type="date"
                      value={String(
                        subscriber.start_date ||
                          subscriber.trial_starts_at ||
                          ""
                      ).slice(0, 10)}
                      onChange={(event) => {
                        handleChange(
                          "start_date",
                          event.target.value
                        );

                        handleChange(
                          "trial_starts_at",
                          event.target.value
                        );
                      }}
                      readOnly={
                        !isEditingSubscription
                      }
                    />
                  </div>

                  <div className="sales-field">
                    <label>Trial End Date</label>

                    <input
                      type="date"
                      value={
                        computedDates.trialEndDate
                      }
                      readOnly
                    />
                  </div>

                  <div className="sales-field">
                    <label>
                      First Payment Date
                    </label>

                    <input
                      type="date"
                      value={
                        computedDates.firstPaymentDate
                      }
                      readOnly
                    />
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Billing Day</label>

                    <input
                      value={
                        computedDates.billingDayLabel
                      }
                      readOnly
                    />
                  </div>

                  {hasScheduledCancellation && (
                    <div className="sales-field sales-field-full">
                      <label>
                        Scheduled Cancellation
                      </label>

                      <input
                        value={
                          formattedScheduledCancelDate
                            ? `Cancels at the end of the current billing period on ${formattedScheduledCancelDate}`
                            : "Cancellation is scheduled; Stripe has not provided a date."
                        }
                        readOnly
                      />
                    </div>
                  )}

                  {isCancelled && (
                    <div className="sales-field sales-field-full">
                      <label>
                        Completed Cancellation
                      </label>

                      <input
                        value={
                          formattedCancelledDate
                            ? `Cancelled on ${formattedCancelledDate}`
                            : "Subscription cancellation completed."
                        }
                        readOnly
                      />
                    </div>
                  )}
                </div>

                <div
                  className="sales-header-actions"
                  style={{ marginTop: "14px" }}
                >
                  {!hasScheduledCancellation &&
                  !isCancelled ? (
                    <button
                      type="button"
                      className="sales-secondary-btn"
                      onClick={
                        handleCancelSubscription
                      }
                      disabled={
                        lifecycleActionLoading
                      }
                    >
                      {lifecycleActionLoading
                        ? "Working..."
                        : "Schedule Cancellation"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="sales-primary-btn"
                      onClick={
                        handleReactivateSubscriber
                      }
                      disabled={
                        lifecycleActionLoading
                      }
                    >
                      {lifecycleActionLoading
                        ? "Working..."
                        : hasScheduledCancellation
                          ? "Undo Scheduled Cancellation"
                          : "Reactivate Subscriber"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <div className="sales-section-card sales-section-subscription">
                <div className="sales-section-head">
                  <h2>Workspace Setup</h2>

                  <p>
                    Domain approval and workspace
                    activation controls.
                  </p>
                </div>

                <div className="sales-form-fields">
                  <div className="sales-field">
                    <label>
                      Provisioning Status
                    </label>

                    <input
                      value={formatStatusLabel(
                        subscriber.provisioning_status
                      )}
                      readOnly
                    />
                  </div>

                  <div className="sales-field">
                    <label>Subdomain</label>

                    <input
                      value={
                        subscriber.subdomain || ""
                      }
                      onChange={(event) => {
                        handleChange(
                          "subdomain",
                          event.target.value.toLowerCase()
                        );

                        setSubdomainStatus(null);
                      }}
                      onBlur={() => checkSubdomain()}
                      placeholder="agencyname"
                    />

                    {checkingSubdomain && (
                      <small>
                        Checking subdomain...
                      </small>
                    )}

                    {!checkingSubdomain &&
                      subdomainStatus?.available && (
                        <small
                          style={{
                            color: "#15803d"
                          }}
                        >
                          Subdomain is available.
                        </small>
                      )}

                    {!checkingSubdomain &&
                      subdomainStatus &&
                      !subdomainStatus.available && (
                        <small
                          style={{
                            color: "#b91c1c"
                          }}
                        >
                          {subdomainStatus.reason ||
                            "Subdomain is unavailable."}
                        </small>
                      )}
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Full Domain</label>

                    <input
                      value={
                        subscriber.full_domain ||
                        (subscriber.subdomain
                          ? `${String(
                              subscriber.subdomain
                            )
                              .trim()
                              .toLowerCase()}.${rootDomain}`
                          : "")
                      }
                      readOnly
                    />
                  </div>
                </div>

                <div
                  className="sales-header-actions"
                  style={{ marginTop: "14px" }}
                >
                  <button
                    type="button"
                    className="sales-secondary-btn"
                    onClick={
                      handleApproveProvisioning
                    }
                    disabled={
                      workspaceActionLoading
                    }
                  >
                    {workspaceActionLoading
                      ? "Working..."
                      : "Approve Domain"}
                  </button>

                  <button
                    type="button"
                    className="sales-secondary-btn"
                    onClick={
                      handleMarkProvisioned
                    }
                    disabled={
                      workspaceActionLoading
                    }
                  >
                    {workspaceActionLoading
                      ? "Working..."
                      : "Mark Workspace Ready"}
                  </button>

                  <button
                    type="button"
                    className="sales-primary-btn"
                    onClick={
                      handleActivateSubscriber
                    }
                    disabled={
                      workspaceActionLoading
                    }
                  >
                    {workspaceActionLoading
                      ? "Working..."
                      : "Go Live"}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <div className="sales-section-card sales-section-subscription">
                <div className="sales-section-head">
                  <div>
                    <h2>
                      Stripe Billing History
                    </h2>

                    <p>
                      Webhook activity received from
                      Stripe for this subscriber.
                    </p>
                  </div>
                </div>

                {billingEventsError ? (
                  <div
                    className="profile-empty-card"
                    style={{ color: "#b91c1c" }}
                  >
                    {billingEventsError}
                  </div>
                ) : billingEventsLoading ? (
                  <div className="profile-empty-card">
                    Loading billing history...
                  </div>
                ) : billingEvents.length === 0 ? (
                  <div className="profile-empty-card">
                    No Stripe billing events have been
                    recorded for this subscriber.
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "900px"
                      }}
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "12px",
                              borderBottom:
                                "1px solid #e2e8f0"
                            }}
                          >
                            Date
                          </th>

                          <th
                            style={{
                              textAlign: "left",
                              padding: "12px",
                              borderBottom:
                                "1px solid #e2e8f0"
                            }}
                          >
                            Event
                          </th>

                          <th
                            style={{
                              textAlign: "left",
                              padding: "12px",
                              borderBottom:
                                "1px solid #e2e8f0"
                            }}
                          >
                            Summary
                          </th>

                          <th
                            style={{
                              textAlign: "left",
                              padding: "12px",
                              borderBottom:
                                "1px solid #e2e8f0"
                            }}
                          >
                            Billing Status
                          </th>

                          <th
                            style={{
                              textAlign: "left",
                              padding: "12px",
                              borderBottom:
                                "1px solid #e2e8f0"
                            }}
                          >
                            Subscription Status
                          </th>

                          <th
                            style={{
                              textAlign: "right",
                              padding: "12px",
                              borderBottom:
                                "1px solid #e2e8f0"
                            }}
                          >
                            Amount
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {billingEvents.map((event) => (
                          <tr
                            key={
                              event.id ||
                              event.stripe_event_id
                            }
                          >
                            <td
                              style={{
                                padding: "12px",
                                borderBottom:
                                  "1px solid #f1f5f9",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {formatBillingEventDate(
                                event.processed_at ||
                                  event.created_at
                              )}
                            </td>

                            <td
                              style={{
                                padding: "12px",
                                borderBottom:
                                  "1px solid #f1f5f9"
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600
                                }}
                              >
                                {formatBillingEventType(
                                  event.stripe_event_type
                                )}
                              </div>

                              <div
                                style={{
                                  marginTop: "4px",
                                  color: "#64748b",
                                  fontSize: "12px"
                                }}
                              >
                                {event.stripe_event_id ||
                                  "No Stripe event ID"}
                              </div>
                            </td>

                            <td
                              style={{
                                padding: "12px",
                                borderBottom:
                                  "1px solid #f1f5f9"
                              }}
                            >
                              {event.event_summary ||
                                "Stripe billing event received"}
                            </td>

                            <td
                              style={{
                                padding: "12px",
                                borderBottom:
                                  "1px solid #f1f5f9"
                              }}
                            >
                              {event.billing_status ? (
                                <span
                                  className={statusClass(
                                    event.billing_status
                                  )}
                                >
                                  {formatStatusLabel(
                                    event.billing_status
                                  )}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>

                            <td
                              style={{
                                padding: "12px",
                                borderBottom:
                                  "1px solid #f1f5f9"
                              }}
                            >
                              {event.subscription_status ? (
                                <span
                                  className={statusClass(
                                    event.subscription_status
                                  )}
                                >
                                  {formatStatusLabel(
                                    event.subscription_status
                                  )}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>

                            <td
                              style={{
                                padding: "12px",
                                borderBottom:
                                  "1px solid #f1f5f9",
                                textAlign: "right",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {formatBillingEventAmount(
                                event
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <ActivityLogPanel
              entityType="subscriber"
              entityId={subscriber.id || id}
              title="Notes & Activity"
              description="Track manual notes and system activity for this subscriber."
            />
          </>
        )}
      </div>
    </>
  );
}

export default SubscriberProfile;